import {
  Box,
  Typography,
  Paper,
  IconButton,
  TextField
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { ContentNodeItemProps } from '@/types';

export default function ContentNodeItem({ 
  node, 
  onEdit, 
  onDelete, 
  onSave, 
  onCancel, 
  isEditing, 
  editPrompt, 
  setEditPrompt,
  validationError
}: ContentNodeItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: node.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleSave = () => {
    onSave(node.id, editPrompt);
  };

  const handleCancel = () => {
    onCancel();
  };

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      sx={{
        padding: 2,
        marginBottom: 2,
        backgroundColor: 'background.paper',
        borderRadius: 2,
        boxShadow: 1,
        border: '1px solid',
        borderColor: 'divider',
        position: 'relative',
        '&:hover': {
          boxShadow: 2,
        }
      }}
    >
      {/* First Row: Drag Handle, Title, and Action Buttons */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 1 }}>
        {/* Drag Handle - only show when not editing */}
        {!isEditing && (
          <Box
            {...attributes}
            {...listeners}
            sx={{
              cursor: 'grab',
              display: 'flex',
              alignItems: 'center',
              padding: '4px',
              '&:hover': {
                backgroundColor: 'action.hover',
                borderRadius: '4px',
              }
            }}
          >
            <DragIcon sx={{ color: 'text.secondary', fontSize: '16px' }} />
          </Box>
        )}

        {/* Content Node Title */}
        <Typography 
          variant="h6" 
          component="h3"
          sx={{ 
            color: 'text.primary', 
            fontWeight: 'bold',
            fontSize: '1rem',
            flex: 1
          }}
        >
          Content Node
        </Typography>
        
        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          {isEditing ? (
            <>
              <IconButton
                onClick={handleSave}
                disabled={!editPrompt.trim()}
                sx={{
                  color: editPrompt.trim() ? 'primary.main' : 'text.disabled',
                  '&:hover': {
                    backgroundColor: editPrompt.trim() ? 'primary.lighter' : 'transparent',
                  },
                  '&.Mui-disabled': {
                    color: 'text.disabled',
                  }
                }}
              >
                <SaveIcon />
              </IconButton>
              <IconButton
                onClick={handleCancel}
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  }
                }}
              >
                <CancelIcon />
              </IconButton>
            </>
          ) : (
            <>
              <IconButton
                onClick={() => onEdit(node)}
                sx={{
                  color: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'primary.lighter',
                  }
                }}
              >
                <EditIcon />
              </IconButton>
              <IconButton
                onClick={() => onDelete(node.id)}
                sx={{
                  color: 'error.main',
                  '&:hover': {
                    backgroundColor: 'error.lighter',
                  }
                }}
              >
                <DeleteIcon />
              </IconButton>
            </>
          )}
        </Box>
      </Box>

      {/* Second Row: Prompt Content */}
      {isEditing ? (
        <>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'text.secondary',
              marginBottom: 1,
              fontSize: '0.875rem',
              fontWeight: '500'
            }}
          >
            Prompt to run on content
          </Typography>
          
          <TextField
            value={editPrompt}
            onChange={(e) => setEditPrompt(e.target.value)}
            multiline
            rows={3}
            variant="outlined"
            fullWidth
            error={!!validationError}
            helperText={validationError}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'background.paper',
                '& fieldset': {
                  borderColor: validationError ? 'error.main' : 'primary.main',
                },
                '& .MuiOutlinedInput-input': {
                  color: 'text.primary',
                  fontSize: '0.875rem',
                }
              }
            }}
          />
        </>
      ) : (
        <Typography 
          variant="body1" 
          sx={{ 
            color: 'text.primary',
            fontSize: '0.875rem',
            lineHeight: 1.5,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word'
          }}
        >
          {node.prompt || 'Enter your prompt here...'}
        </Typography>
      )}
    </Paper>
  );
}
